import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useStytchSession, useStytch } from "@stytch/react";
import TchrURL from "../../TchrURL";
import { checkResponseStatus } from "@/lib/utils/responseChecker";
import i18n from "i18next";

export const getClientKey = (): string => {
  const STORAGE_KEY = "tchr_client_key";
  let clientKey = localStorage.getItem(STORAGE_KEY);

  if (!clientKey) {
    // Generate a new GUID
    clientKey = crypto.randomUUID();
    localStorage.setItem(STORAGE_KEY, clientKey);
  }

  return clientKey;
};

export interface UserProgress {
  registered?: string;
  nameSet?: string;
  subscribed?: string;
  referred?: string;
  extraTrialReceived?: string;
  creditReceived?: string;
}

export interface UserStats {
  id: string;
  lastUpdated: string;
  creationDate: string;
  isDeleted: boolean;
  deletionDate?: string;
  clientKey: string;
  userId?: string;
  toolUses: Record<string, number>;
  referrals: number;
  progress: UserProgress;
}

interface UseUserStatsOptions {
  enabled?: boolean;
}

// Helper function to invalidate and refetch user stats
export const useInvalidateUserStats = () => {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: ["user", "stats"] });
    queryClient.refetchQueries({ queryKey: ["user", "stats"] });
  };
};

const useUserStats = (options?: UseUserStatsOptions) => {
  const { session } = useStytchSession();
  const stytch = useStytch();

  return useQuery<UserStats, Error>({
    queryKey: ["user", "stats"],
    queryFn: async () => {
      const url = new TchrURL("/user/stats");
      const clientKey = getClientKey();

      const headers: HeadersInit = {
        "clientKey": clientKey,
        "Content-Type": "application/json",
      };

      // Add auth header if user is authenticated
      if (session) {
        const tokens = stytch.session.getTokens();
        if (tokens?.session_jwt) {
          headers.Authorization = `Bearer ${tokens.session_jwt}`;
        }
      }

      const response = await fetch(url, {
        method: "GET", headers: {
          ...headers,
        }
      });
      checkResponseStatus(
        response,
        i18n.getFixedT?.(null, ["errors"]) || (i18n.t.bind(i18n) as any),
        { context: "userStats" }
      );

      return response.json();
    },
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

export default useUserStats;